// Formatting helpers. Backend timestamps are MICROSECONDS since epoch (common/03).

/** Microsecond epoch → local date-time string (zh-Hant locale default). */
export function formatTimestampUs(us: number | string | null | undefined, opts?: Intl.DateTimeFormatOptions): string {
  if (us === null || us === undefined || us === '') return '—';
  const n = typeof us === 'string' ? Number(us) : us;
  if (!Number.isFinite(n)) return '—';
  const date = new Date(n / 1000);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...opts,
  });
}

/** Microsecond epoch → Date. */
export function dateFromUs(us: number): Date {
  return new Date(us / 1000);
}

/** Second epoch → local date-time string. Some endpoints report seconds (e.g. deletion `cooling_until_s`). */
export function formatTimestampS(s: number | string | null | undefined, opts?: Intl.DateTimeFormatOptions): string {
  if (s === null || s === undefined || s === '') return '—';
  const n = typeof s === 'string' ? Number(s) : s;
  if (!Number.isFinite(n)) return '—';
  return formatTimestampUs(n * 1_000_000, opts);
}

/** Seconds → "m:ss" / "h:mm:ss" (player-style duration). */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Microsecond DURATION (not epoch) → "m:ss" etc. */
export function formatDurationUs(us: number | null | undefined): string {
  if (us === null || us === undefined || !Number.isFinite(us)) return '—';
  return formatDuration(us / 1_000_000);
}

/** Bytes → human readable (binary, 1 decimal): 1536 → "1.5 KiB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let value = bytes;
  let i = -1;
  do {
    value /= 1024;
    i += 1;
  } while (value >= 1024 && i < units.length - 1);
  return `${value.toFixed(1)} ${units[i]}`;
}

/** Relative time from microsecond epoch ("3 分鐘前"); falls back to absolute beyond 7 days. */
export function formatRelativeUs(us: number | string | null | undefined): string {
  if (us === null || us === undefined || us === '') return '—';
  const n = typeof us === 'string' ? Number(us) : us;
  if (!Number.isFinite(n)) return '—';
  const diffMs = Date.now() - n / 1000;
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (abs < 60_000) return rtf.format(-Math.round(diffMs / 1000), 'second');
  if (abs < 3_600_000) return rtf.format(-Math.round(diffMs / 60_000), 'minute');
  if (abs < 86_400_000) return rtf.format(-Math.round(diffMs / 3_600_000), 'hour');
  if (abs < 7 * 86_400_000) return rtf.format(-Math.round(diffMs / 86_400_000), 'day');
  return formatTimestampUs(n, { second: undefined });
}
